import { LANGUAGE_DISPLAY_NAMES } from '@shared/core/localized-defaults'
import classNames from 'classnames'
import { Info } from 'lucide-react'
import { type MouseEvent, ReactNode, useState } from 'react'
import { createPortal } from 'react-dom'
import { FormattedMessage, useIntl } from 'react-intl'
import { Link, useNavigate } from 'react-router-dom'
import { FormattedText, InfoModal, Typography } from 'src/modules/core/components'
import type { MessageTemplateOrigin } from 'src/modules/message-templates/types/message-template.types'
import { buildMessageLanguageSettingsPath } from 'src/modules/user/components/client-message-language-card/client-message-language-card.domain'
import { useMessageLanguageSourceText } from 'src/modules/user/hooks/use-message-language-source-text'
import type { ChangeLanguageLinkProps, MessageLanguageNoticeProps } from './message-language-notice.interface'
import classes from './message-language-notice.module.scss'

type NoticeCopy = {
  summaryId: string
  detail: string | null
  linkId: string
}

const CHANGE_LANGUAGE_ID = 'settings.messageLanguage.notice.changeLanguage'
const CHANGE_ACCOUNT_LANGUAGE_ID = 'settings.messageLanguage.notice.changeAccountLanguage'
const SUMMARY_ID = 'settings.messageLanguage.notice.summary'
const TEMPLATE_SOURCE_IDS: Record<MessageTemplateOrigin, string> = {
  event: 'settings.messageLanguage.notice.sourceEventTemplate',
  calendar: 'settings.messageLanguage.notice.sourceCalendarTemplate',
  account: 'settings.messageLanguage.notice.sourceAccountTemplate',
}

function joinSentences(...sentences: (string | null)[]): string | null {
  const present = sentences.filter(Boolean)
  return present.length > 0 ? present.join(' ') : null
}

function useNoticeCopy({
  source,
  countryCode,
  templateOrigin,
  accountLanguage,
}: Pick<MessageLanguageNoticeProps, 'source' | 'countryCode' | 'templateOrigin' | 'accountLanguage'>): NoticeCopy {
  const { formatMessage } = useIntl()
  const sourceText = useMessageLanguageSourceText(source, countryCode)
  const accountSourceText = useMessageLanguageSourceText(
    accountLanguage?.source ?? 'default',
    accountLanguage?.countryCode ?? null,
  )

  if (!accountLanguage) {
    if (source !== 'template') return { summaryId: SUMMARY_ID, detail: sourceText, linkId: CHANGE_LANGUAGE_ID }
    return {
      summaryId: SUMMARY_ID,
      detail: formatMessage({ id: TEMPLATE_SOURCE_IDS[templateOrigin ?? 'event'] }),
      linkId: CHANGE_ACCOUNT_LANGUAGE_ID,
    }
  }
  if (source !== 'template') {
    return {
      summaryId: SUMMARY_ID,
      detail: joinSentences(formatMessage({ id: 'settings.messageLanguage.notice.yourAccountLanguage' }), sourceText),
      linkId: CHANGE_ACCOUNT_LANGUAGE_ID,
    }
  }
  const accountLanguageIs = formatMessage(
    { id: 'settings.messageLanguage.notice.accountLanguageIs' },
    { language: LANGUAGE_DISPLAY_NAMES[accountLanguage.language].english },
  )
  return {
    summaryId: 'settings.messageLanguage.notice.templateSummary',
    detail: joinSentences(accountLanguageIs, accountLanguage.source === 'account' ? null : accountSourceText),
    linkId: CHANGE_ACCOUNT_LANGUAGE_ID,
  }
}

function isPlainLeftClick(event: MouseEvent): boolean {
  return event.button === 0 && !event.metaKey && !event.ctrlKey && !event.shiftKey && !event.altKey
}

// Portaled to body: inside the host <form> its buttons would submit it, and the drawer would cover it.
function ChangeLanguageLink({ to, labelId, hasUnsavedChanges }: ChangeLanguageLinkProps) {
  const navigate = useNavigate()
  const { formatMessage } = useIntl()
  const [isConfirmOpen, setIsConfirmOpen] = useState(false)
  const handleClick = (event: MouseEvent<HTMLAnchorElement>) => {
    if (!hasUnsavedChanges || !isPlainLeftClick(event)) return
    event.preventDefault()
    setIsConfirmOpen(true)
  }
  return (
    <>
      <Link to={to} onClick={handleClick} className={classes.link}>
        <FormattedText id={labelId} />
      </Link>
      {isConfirmOpen &&
        createPortal(
          <InfoModal
            title={formatMessage({ id: 'settings.messageLanguage.notice.leaveTitle' })}
            description={formatMessage({ id: 'settings.messageLanguage.notice.leaveDescription' })}
            button={formatMessage({ id: 'settings.messageLanguage.notice.leaveConfirm' })}
            closeButton={formatMessage({ id: 'settings.messageLanguage.notice.leaveCancel' })}
            onClose={() => setIsConfirmOpen(false)}
            onClick={() => navigate(to)}
          />,
          document.body,
        )}
    </>
  )
}

export function MessageLanguageNotice({
  language,
  source,
  countryCode,
  templateOrigin,
  usageMode,
  accountLanguage,
  hasUnsavedChanges = false,
  className,
}: MessageLanguageNoticeProps) {
  const { summaryId, detail, linkId } = useNoticeCopy({ source, countryCode, templateOrigin, accountLanguage })
  return (
    <div role="note" className={classNames(classes.notice, className)} data-testid="message-language-notice">
      <span className={classes.iconBadge} aria-hidden="true">
        <Info size={16} strokeWidth={2.2} />
      </span>
      <div className={classes.body}>
        <Typography variant="body" className={classes.summary}>
          <FormattedMessage
            id={summaryId}
            values={{
              language: LANGUAGE_DISPLAY_NAMES[language].english,
              strong: (chunks: ReactNode) => <strong>{chunks}</strong>,
            }}
          />
        </Typography>
        <Typography variant="body" className={classes.source}>
          {detail && <span>{detail} </span>}
          <ChangeLanguageLink
            to={buildMessageLanguageSettingsPath(usageMode)}
            labelId={linkId}
            hasUnsavedChanges={hasUnsavedChanges}
          />
        </Typography>
      </div>
    </div>
  )
}
